import { useState, useRef, useEffect } from 'react'
import { MagnifierIcon } from '../icons/general.tsx'
import { IconType } from '../icons/types.ts'
import { useClickOutside } from '../../../hook/useClickOutside.tsx'
import { useDispatch, useSelector } from 'react-redux'
import {
  startSelectGlobalProgram,
  startSelectProgram,
  startSetPrograms,
} from '@/storage/programs/thunk.ts'
import { AppDispatch, RootState } from '@/storage/store.ts'
import {
  GlobalProgramProps,
  GlobalProgramsResponseProps,
  ProgramYearsProps,
} from '@/queries/program/schemas.ts'
import { getGlobalPrograms } from '@/queries/program/requests.ts'

export default function ProgramSelect() {
  const [isOpen, setIsOpen] = useState(false)
  const [schoolYearOpen, setSchoolYearOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const dropdownYearRef = useRef<HTMLDivElement>(null)
  const dispatch = useDispatch<AppDispatch>()

  const { programs, selectedProgram, programYears, selectedProgramYear } =
    useSelector((s: RootState) => s.programs)

  useClickOutside(dropdownRef, setIsOpen)
  useClickOutside(dropdownYearRef, setSchoolYearOpen)

  const [searchTerm, setSearchTerm] = useState('')
  const filteredPrograms = programs.filter((program: GlobalProgramProps) =>
    program.name.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const getAllGlobalPrograms = async () => {
    const programQueryResult: GlobalProgramsResponseProps =
      await getGlobalPrograms()
    dispatch(startSetPrograms(programQueryResult))
  }

  const handleSelectProgram = (program: GlobalProgramProps) => {
    dispatch(startSelectGlobalProgram(program))
    setIsOpen(false)
    setSearchTerm('')
  }

  const handleYearProgram = (program: ProgramYearsProps) => {
    dispatch(startSelectProgram(programs[0], program))
    setSchoolYearOpen(false)
  }

  useEffect(() => {
    if (!selectedProgramYear) {
      getAllGlobalPrograms()
    }
  }, [])

  return (
    <>
      <div className="relative w-52" ref={dropdownRef}>
        <div
          className="flex items-center justify-between w-full rounded-md border-0 bg-white py-1.5 pl-3 pr-2 font-medium text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6 cursor-pointer"
          onClick={() => setIsOpen(!isOpen)}
        >
          <span>{selectedProgram?.name ?? 'Choose a Program'}</span>
          <button
            className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none"
            id="headlessui-combobox-button"
            type="button"
            aria-haspopup="listbox"
            aria-expanded={isOpen}
          >
            <i className="ki-down ki-filled text-lg"></i>
          </button>
        </div>

        {isOpen && (
          <div className="absolute mt-1 w-full bg-white rounded-t-md shadow-lg ring-1 ring-gray-300 rounded-b-md">
            <div className="relative shadow-sm">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <MagnifierIcon type={IconType.Outline} />
              </div>
              <input
                type="text"
                placeholder="Search..."
                className="pl-10 w-full h-10 rounded-t-md border-none focus:border-none active:border-none focus:ring-0 active:ring-0"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <hr />

            <div className="p-2 text-gray-500">Programs:</div>

            <ul className="max-h-80 overflow-y-auto rounded-b-md">
              {filteredPrograms.map((program: GlobalProgramProps) => (
                <li
                  key={program.id}
                  className="flex items-center justify-between px-4 py-2 cursor-pointer text-gray-900 hover:bg-primary-600 hover:text-white group"
                  onClick={() => handleSelectProgram(program)}
                >
                  <span className="font-semibold">{program.name}</span>
                  {selectedProgram?.id === program.id && (
                    <i
                      className="ki-filled ki-check ml-2 text-primary-500 group-hover:text-white"
                      aria-hidden="true"
                    ></i>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      {!(
        location.pathname.includes('roles') ||
        location.pathname.includes('users') ||
        location.pathname.includes('tasks')
      ) && (
        <div className="relative w-52" ref={dropdownYearRef}>
          <div
            className="flex items-center justify-between w-full rounded-md border-0 bg-white py-1.5 pl-3 pr-2 font-medium text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6 cursor-pointer"
            onClick={() => setSchoolYearOpen(!schoolYearOpen)}
          >
            <span>{selectedProgramYear?.period ?? 'xxxx-xx-xx'}</span>
            <button
              className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none"
              id="headlessui-combobox-button"
              type="button"
              aria-haspopup="listbox"
              aria-expanded={schoolYearOpen}
            >
              <i className="ki-down ki-filled text-lg"></i>
            </button>
          </div>

          {schoolYearOpen && (
            <div className="absolute mt-1 w-full bg-white rounded-t-md shadow-lg ring-1 ring-gray-300 rounded-b-md">
              <hr />

              <div className="p-2 text-gray-500">Years:</div>

              <ul className="max-h-80 overflow-y-auto rounded-b-md">
                {programYears.map((program, index) => (
                  <li
                    key={index}
                    className="flex items-center justify-between px-4 py-2 cursor-pointer text-gray-900 hover:bg-primary-600 hover:text-white group"
                    onClick={() => handleYearProgram(program)}
                  >
                    <span className="font-semibold">{program.period}</span>
                    {selectedProgramYear?.period === program.period && (
                      <i
                        className="ki-filled ki-check ml-2 text-primary-500 group-hover:text-white"
                        aria-hidden="true"
                      ></i>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </>
  )
}
